import type { PullTestResultsStep } from './project';

export interface PullTestResultsDetailRow {
  label: string;
  value: string;
}

/** One row in the read-only pull test results table. */
export interface PullTestResultsTableRow {
  loadLabel: string;
  d1: string;
  d2: string;
  d3?: string;
}

export type PullTestResultsModalityKey = 'tensile' | 'lateral' | 'compression';

export interface PullTestResultsModalitySpec {
  key: PullTestResultsModalityKey;
  label: string;
  accentColor: string;
  resultsKey: 'tensileTestResults' | 'lateralTestResults' | 'compressionTestResults';
}

export const PULL_TEST_RESULTS_MODALITY_SPECS: readonly PullTestResultsModalitySpec[] = [
  {
    key: 'tensile',
    label: 'Tensile (Uplift)',
    accentColor: '#e8a020',
    resultsKey: 'tensileTestResults',
  },
  {
    key: 'lateral',
    label: 'Lateral',
    accentColor: '#2abfaa',
    resultsKey: 'lateralTestResults',
  },
  {
    key: 'compression',
    label: 'Compression',
    accentColor: '#9b72db',
    resultsKey: 'compressionTestResults',
  },
];

export interface PullTestResultsDisplaySection {
  key: PullTestResultsModalityKey;
  label: string;
  accentColor: string;
  deflectionCount: 2 | 3;
  steps: PullTestResultsTableRow[];
  testedByName?: string;
  testTimestampLabel?: string;
  modalityStatus?: string;
  /** Lateral-only metadata shown above the table. */
  extras: PullTestResultsDetailRow[];
  /** Step comments rendered below the table so compare columns stay row-aligned. */
  stepComments?: string;
}

export interface PullTestCollisionCompareColumn {
  title: string;
  submissionSide: 'server' | 'conflicting';
  scalarRows: PullTestResultsDetailRow[];
  testSections: PullTestResultsDisplaySection[];
}

function isPullTestResultsStep(value: unknown): value is PullTestResultsStep {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const order = (value as PullTestResultsStep).order;
  return typeof order === 'number' && Number.isFinite(order);
}

export function parsePullTestResultsSteps(value: unknown): PullTestResultsStep[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isPullTestResultsStep).sort((a, b) => a.order - b.order);
}

/** Whether any step records a third deflection gauge reading. */
export function inferPullTestResultsStepDeflectionCount(
  steps: readonly PullTestResultsStep[]
): 2 | 3 {
  const hasThird = steps.some(step => {
    const v = step.deflection3In;
    if (v === null || v === undefined) {
      return false;
    }
    return Number.isFinite(Number(v));
  });
  return hasThird ? 3 : 2;
}

export function formatPullTestResultsDeflectionInches(
  value: number | null | undefined
): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '—';
  }
  return `${Number(value).toFixed(4)}"`;
}

export function mapPullTestResultsStepsToTableRows(
  steps: readonly PullTestResultsStep[]
): { deflectionCount: 2 | 3; rows: PullTestResultsTableRow[] } {
  const deflectionCount = inferPullTestResultsStepDeflectionCount(steps);
  const rows = [...steps]
    .sort((a, b) => a.order - b.order)
    .map(step => ({
      loadLabel: `${Number(step.appliedLoadLbs ?? 0).toLocaleString()} lbs`,
      d1: formatPullTestResultsDeflectionInches(step.deflection1In),
      d2: formatPullTestResultsDeflectionInches(step.deflection2In),
      ...(deflectionCount === 3
        ? { d3: formatPullTestResultsDeflectionInches(step.deflection3In) }
        : {}),
    }));
  return { deflectionCount, rows };
}

/** One-line summary for a captured load/deflection step (fallback text views). */
export function formatPullTestResultsStepSummary(
  step: PullTestResultsStep,
  deflectionCount: 2 | 3 = inferPullTestResultsStepDeflectionCount([step])
): string {
  const parts: string[] = [];
  if (step.appliedLoadLbs != null && Number.isFinite(step.appliedLoadLbs)) {
    parts.push(`${step.appliedLoadLbs.toLocaleString()} lbs`);
  } else {
    parts.push('— lbs');
  }
  parts.push(`D1 ${formatPullTestResultsDeflectionInches(step.deflection1In)}`);
  parts.push(`D2 ${formatPullTestResultsDeflectionInches(step.deflection2In)}`);
  if (deflectionCount === 3) {
    parts.push(`D3 ${formatPullTestResultsDeflectionInches(step.deflection3In)}`);
  }
  const comments = step.comments?.trim();
  if (comments) {
    parts.push(`Comment: ${comments}`);
  }
  return parts.join(' · ');
}

/** Expands `*TestResults.steps` into label/value rows for simple detail views. */
export function pullTestResultsStepsToDetailRows(
  steps: unknown,
  prefix: string
): PullTestResultsDetailRow[] {
  const parsed = parsePullTestResultsSteps(steps);
  if (parsed.length === 0) {
    return [];
  }

  const deflectionCount = inferPullTestResultsStepDeflectionCount(parsed);
  const labelPrefix = prefix.trim() || 'Test';

  return parsed.map(step => ({
    label: `${labelPrefix} · Step ${step.order}`,
    value: formatPullTestResultsStepSummary(step, deflectionCount),
  }));
}

export function buildPullTestResultsDisplaySection(
  spec: PullTestResultsModalitySpec,
  results: Record<string, unknown> | null | undefined,
  options?: {
    testedByName?: string;
    testTimestampLabel?: string;
  }
): PullTestResultsDisplaySection | null {
  if (!results) {
    return null;
  }

  const steps = parsePullTestResultsSteps(results['steps']);
  if (steps.length === 0) {
    return null;
  }

  const { deflectionCount, rows } = mapPullTestResultsStepsToTableRows(steps);
  const extras: PullTestResultsDetailRow[] = [];
  const modalityStatus = results['status'];

  if (spec.key === 'lateral') {
    const loadHeight = results['loadHeightAboveGradeFt'];
    if (typeof loadHeight === 'number' && Number.isFinite(loadHeight)) {
      extras.push({ label: 'Load height above grade', value: `${loadHeight} ft` });
    }
    const gaugeHeight = results['gaugeHeightIn'];
    if (typeof gaugeHeight === 'number' && Number.isFinite(gaugeHeight)) {
      extras.push({ label: 'Gauge height', value: `${gaugeHeight.toFixed(4)}"` });
    }
  }

  const stepCommentLines = steps
    .map(step => {
      const comment = step.comments?.trim();
      if (!comment) {
        return null;
      }
      return `Step ${step.order}: ${comment}`;
    })
    .filter((line): line is string => !!line);

  return {
    key: spec.key,
    label: spec.label,
    accentColor: spec.accentColor,
    deflectionCount,
    steps: rows,
    testedByName: options?.testedByName,
    testTimestampLabel: options?.testTimestampLabel,
    modalityStatus: typeof modalityStatus === 'string' ? modalityStatus : undefined,
    extras,
    stepComments: stepCommentLines.length > 0 ? stepCommentLines.join(' · ') : undefined,
  };
}
